import React from 'react';
import Button from '../../../../components/base/Button';

const PricingSection: React.FC = () => {
  const plans = [
    {
      name: 'Essential',
      price: '$299',
      period: '/month',
      description: 'Perfect for those starting their hormone optimization journey',
      features: [
        'Comprehensive initial consultation',
        'Basic hormone panel testing',
        'Single hormone therapy',
        'Monthly follow-up appointments',
        'Email support',
        'Treatment monitoring'
      ],
      popular: false,
      color: 'from-blue-500 to-cyan-500'
    },
    {
      name: 'Comprehensive',
      price: '$499',
      period: '/month',
      description: 'Most popular choice for complete hormone optimization',
      features: [
        'Everything in Essential plan',
        'Advanced hormone panel testing',
        'Multi-hormone therapy protocols',
        'Bi-weekly consultations',
        'Priority phone support',
        'Nutritional counseling',
        'Supplement recommendations',
        'Lifestyle optimization coaching'
      ],
      popular: true,
      color: 'from-pink-500 to-purple-500'
    },
    {
      name: 'Premium',
      price: '$799',
      period: '/month',
      description: 'Ultimate hormone optimization with concierge-level care',
      features: [
        'Everything in Comprehensive plan',
        'Weekly consultations',
        '24/7 direct physician access',
        'Advanced peptide therapy add-ons',
        'Genetic testing and analysis',
        'Personalized fitness planning',
        'Stress management coaching',
        'VIP treatment scheduling'
      ],
      popular: false,
      color: 'from-purple-500 to-indigo-500'
    }
  ];

  const addOns = [
    {
      name: 'Advanced Lab Panel',
      price: '$199',
      description: 'Comprehensive metabolic and inflammatory markers'
    },
    {
      name: 'Genetic Testing',
      price: '$299',
      description: 'Personalized treatment based on genetic profile'
    },
    {
      name: 'Nutritional Counseling',
      price: '$149',
      description: 'Monthly sessions with certified nutritionist'
    },
    {
      name: 'Peptide Therapy Add-on',
      price: '$399',
      description: 'Complementary peptide treatments for enhanced results'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Investment in Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-600 to-purple-600">Health</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Choose the plan that best fits your needs and budget. All plans include ongoing medical supervision 
            and personalized care from our hormone optimization specialists.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {plans.map((plan, index) => (
            <div key={index} className={`relative bg-white rounded-2xl shadow-lg overflow-hidden ${plan.popular ? 'ring-2 ring-purple-500 transform scale-105' : ''}`}>
              {plan.popular && (
                <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-pink-500 to-purple-500 text-white text-center py-2 text-sm font-semibold">
                  MOST POPULAR
                </div>
              )}
              
              <div className="p-8">
                <div className={`w-16 h-16 bg-gradient-to-r ${plan.color} rounded-full flex items-center justify-center mx-auto mb-6`}>
                  <i className="ri-heart-pulse-line text-2xl text-white"></i>
                </div>
                
                <h3 className="text-2xl font-bold text-gray-900 text-center mb-2">{plan.name}</h3>
                <p className="text-gray-600 text-center mb-6">{plan.description}</p>
                
                <div className="text-center mb-8">
                  <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                  <span className="text-gray-600">{plan.period}</span>
                </div>
                
                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <i className="ri-check-line text-green-600 mr-3"></i>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Button 
                  variant={plan.popular ? "primary" : "outline"} 
                  size="lg" 
                  className={`w-full ${plan.popular ? 'bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700' : ''}`}
                >
                  Get Started
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gray-50 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
            Optional Add-On Services
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {addOns.map((addOn, index) => (
              <div key={index} className="bg-white rounded-xl p-6 text-center shadow-sm">
                <h4 className="font-semibold text-gray-900 mb-2">{addOn.name}</h4>
                <p className="text-2xl font-bold text-purple-600 mb-3">{addOn.price}</p>
                <p className="text-sm text-gray-600">{addOn.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 text-center">
          <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Not Sure Which Plan is Right for You?
            </h3>
            <p className="text-gray-600 mb-6">
              Schedule a free consultation to discuss your goals and get personalized recommendations.
            </p>
            <Button variant="outline" size="lg">
              <i className="ri-phone-line mr-2"></i>
              Schedule Free Consultation
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;